import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DevAuthToolbar } from './DevAuthToolbar';
import { getDevAuthToolbarData, setDevUser } from '../utils/devAuth';
import { useAuth } from '../hooks/useAuth';
import { hasAdminAccess } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';
import { Avatar } from './Avatar';
import { ApiOfflineBanner } from './ApiOfflineBanner';
import { VerificationBanner } from './VerificationBanner';
import { Footer } from './Footer';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import { MobileMenuPanel } from './layout/MobileMenuPanel';
import { adminMenuItems, moreMenuItems, FOOTER_HIDDEN_PREFIXES } from './layout/navConfig';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const devAuthData = getDevAuthToolbarData();
  const hasDevToolbar = devAuthData !== null;
  const { user } = useAuth();
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Enable swipe-left navigation globally
  useSwipeNavigation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setIsAdminMenuOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setIsAdminMenuOpen(false);
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
    setIsMoreMenuOpen(false);
  }, [location.pathname]);

  // Helper function to determine if a route is active
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Helper function to get nav link classes
  const getNavLinkClasses = (path: string) => {
    const baseClasses = 'inline-flex items-center whitespace-nowrap px-1 pt-1 pb-1 border-b-2 text-base font-sans font-semibold';
    const activeClasses = 'border-primary-700 text-gray-900';
    const inactiveClasses = 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900';

    return `${baseClasses} ${isActiveRoute(path) ? activeClasses : inactiveClasses}`;
  };

  // Helper function to get mobile nav link classes.
  // Active state uses background tint + ink-dark text, NOT a side-stripe
  // border. Side-stripe borders (border-l > 1px) are an AI-slop tell and
  // are banned by the project design principles.
  const getMobileNavLinkClasses = (path: string) => {
    const baseClasses = 'block px-4 py-2 text-lg font-semibold transition-colors';
    const activeClasses = 'bg-gray-100 text-gray-900';
    const inactiveClasses = 'text-gray-600 hover:bg-gray-100 hover:text-gray-900';

    return `${baseClasses} ${isActiveRoute(path) ? activeClasses : inactiveClasses}`;
  };

  const isMoreActive = moreMenuItems.some(
    (item) => 'path' in item && isActiveRoute(item.path)
  );

  const logoutMutation = trpc.auth.logout.useMutation({
    onSettled: () => {
      // Clear production session cookie
      document.cookie = 'authjs.session-token=; Max-Age=0; path=/';
      // Clear dev-auth identity so the x-dev-user header is not re-sent
      // after reload — without this, logout is a no-op in dev mode.
      setDevUser(null);
      // Full reload to reset app state
      window.location.href = '/';
    },
  });

  const showFooter = !FOOTER_HIDDEN_PREFIXES.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <DevAuthToolbar />
      <nav className={`bg-gray-50 border-b border-gray-200 ${hasDevToolbar ? 'mt-16' : 'mt-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/" className="flex items-center">
                <img src="/winex-logo.png" alt="WineryXplorer" className="h-4 sm:h-8 w-auto" />
              </Link>
              <div className="hidden lg:ml-10 lg:flex lg:items-center lg:space-x-7">
                <Link
                  to="/wineries"
                  className={getNavLinkClasses('/wineries')}
                >
                  Wineries
                </Link>
                <Link
                  to="/experiences"
                  className={getNavLinkClasses('/experiences')}
                >
                  Experiences
                </Link>
                <Link
                  to="/events"
                  className={getNavLinkClasses('/events')}
                >
                  Events
                </Link>
                <Link
                  to="/itineraries"
                  className={getNavLinkClasses('/itineraries')}
                >
                  Itineraries
                </Link>
                <div className="relative flex items-center" ref={moreMenuRef}>
                  <button
                    onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                    className={`inline-flex items-center whitespace-nowrap px-1 pt-1 pb-1 border-b-2 text-base font-sans font-semibold cursor-pointer bg-transparent ${
                      isMoreActive
                        ? 'border-primary-700 text-gray-900'
                        : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                    aria-haspopup="menu"
                    aria-expanded={isMoreMenuOpen}
                  >
                    More
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isMoreMenuOpen && (
                    <div className="absolute left-0 top-full mt-0 w-48 rounded-md shadow-lg bg-gray-50 ring-1 ring-gray-200 z-50">
                      <div className="py-1">
                        {moreMenuItems.map((item, index) =>
                          'hr' in item ? (
                            <div
                              key={`hr-${index}`}
                              className="my-1 border-t border-gray-200"
                              role="separator"
                            />
                          ) : (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`block px-4 py-2 text-sm ${
                                isActiveRoute(item.path)
                                  ? 'bg-gray-100 text-gray-900'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {user && hasAdminAccess(user.role) && (
                  <div className="relative flex items-center" ref={adminMenuRef}>
                    <button
                      onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                      className={`${getNavLinkClasses('/admin')} cursor-pointer bg-transparent border-none`}
                    >
                      Admin
                      <svg
                        className={`ml-1 h-4 w-4 transition-transform ${isAdminMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isAdminMenuOpen && (
                      <div className="absolute left-0 top-full mt-0 w-48 rounded-md shadow-lg bg-gray-50 ring-1 ring-gray-200 z-50">
                        <div className="py-1">
                          {adminMenuItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`block px-4 py-2 text-sm ${
                                location.pathname === item.path
                                  ? 'bg-gray-100 text-gray-900'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Sign In Button for Anonymous Users (hidden at extreme-narrow,
                where it lives in the hamburger menu instead — TripAdvisor /
                Airbnb pattern) */}
            {!user && (
              <div className="hidden lg:flex lg:items-center">
                <Link
                  to="/login"
                  className="inline-flex items-center whitespace-nowrap shrink-0 px-3 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-primary-700 hover:bg-primary-800"
                >
                  Sign In
                </Link>
              </div>
            )}
            {/* User Profile Dropdown */}
            {user && (
              <div className="hidden lg:flex lg:items-center relative" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="inline-flex items-center gap-2 px-2 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none rounded-full hover:bg-gray-100 transition-colors"
                >
                  <Avatar user={user} size="md" />
                  <svg
                    className={`h-4 w-4 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 top-full mt-0 w-48 rounded-md shadow-lg bg-gray-50 ring-1 ring-gray-200 z-50">
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Profile
                      </Link>
                      <div className="border-t border-gray-200" />
                      <button
                        onClick={() => logoutMutation.mutate()}
                        disabled={logoutMutation.isPending}
                        className="block w-full text-left px-4 py-2 text-sm text-error hover:bg-gray-100 cursor-pointer bg-transparent border-none"
                      >
                        {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Mobile hamburger button */}
            <div className="flex items-center lg:hidden">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-700"
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu panel */}
        {isMobileMenuOpen && (
          <MobileMenuPanel
            user={user}
            getMobileNavLinkClasses={getMobileNavLinkClasses}
            onLogout={() => logoutMutation.mutate()}
            isLoggingOut={logoutMutation.isPending}
          />
        )}
      </nav>
      <ApiOfflineBanner />
      <VerificationBanner />
      <main className="flex-1 w-full max-w-7xl mx-auto py-0 sm:py-3 sm:px-6 lg:px-8">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};
